/**
 * VAP (Video Animation Player) Data Types
 *
 * Original project by Tencent VAP Team
 * GitHub: https://github.com/Tencent/vap
 * Documentation: https://github.com/Tencent/vap/blob/master/tool/JsonDesc.md
 *
 * This file defines the data structures for VAP video format
 */

import { _decorator, JsonAsset, VideoClip } from 'cc';

const { ccclass, property } = _decorator;

export enum VapSrcType {
    img = 'img',
    txt = 'txt',
}

export enum VapOrienType {
    all = 0,
    portrait = 1,
    landscape = 2,
}

export enum VapfitType {
    fitXY = 'fitXY', // 按指定尺寸縮放
    centerFull = 'centerFull', // 默認資源尺寸展示
}

export enum VapTxtStyle {
    none = '',
    bold = 'b',
}
/** Vap影片資訊 */
export interface IVapData {
    /** vap影片基本資訊 */
    info: {
        /** version */
        v: number;
        /** 寬 (最終渲染尺寸) */
        w: number;
        /** 高 (最終渲染尺寸) */
        h: number;
        /** 影片幀數 */
        fps: number;
        /** 素材影片寬 */
        videoW: number;
        /** 素材影片高 */
        videoH: number;
        /** alpha在素材影片中位置[x,y,width,height] */
        aFrame: number[];
        /** rgb在素材影片中位置[x,y,width,height] */
        rgbFrame: number[];
        /** 適用於螢幕方向,0:兼容 1:直向 2:橫向 */
        orien: VapOrienType;
        /** 是否融合特效 */
        isVapx: boolean;
    };
    /** vap各合成物件資訊 */
    src: IVapSourceData[];
    /** vap合成物件每幀的資訊 */
    frame: IVapSrcFrameData[];
}

/** Vap合成物件資訊 */
export interface IVapSourceData {
    /** 合成物件ID */
    srcId: string;
    /** 合成物件類型(img or txt) */
    srcType: VapSrcType;
    /** 合成物件載入方式 */
    loadType: string;
    /** 合成物件標籤 */
    srcTag: string;
    /** 合成物件寬 */
    w: number;
    /** 合成物件高 */
    h: number;
    /** 合成物件適應方式 */
    fitType: VapfitType;
    /** 文字顏色(optional) */
    color?: string;
    /** 文字樣式(optional) */
    style?: VapTxtStyle;
}

/** Vap合成物件每幀的資訊 */
export interface IVapSrcFrameData {
    /** 幀index */
    i: number;
    /** 幀內所有合成物件資訊 */
    obj: {
        /** 合成物件ID */
        srcId: string;
        /** 合成物件渲染順序 */
        z: number;
        /** 渲染rect [x,y,width,height] */
        frame: number[];
        /** 遮罩rect [x,y,width,height] */
        mFrame: number[];
        /** 順時針旋轉transform */
        mt: number; // v2支援0/90度
    }[];
}

export type VapRemoteData = {
    name: string;
    videoUrl: string;
    configUrl: string;
};

@ccclass('VapLocalData')
export class VapLocalData {
    @property(VideoClip)
    public clip: VideoClip = null;
    @property(JsonAsset)
    public readonly jsonConfig: JsonAsset = null;
}

export function toUvArea(frame: number[], texW: number, texH: number): number[] {
    const [x, y, w, h] = frame;
    if (!texW || !texH) {
        return [0, 0, 1, 1];
    }
    return [x / texW, y / texH, w / texW, h / texH];
}
